// Майстор: запитвания, оферта (цена + срок), "отстранено" със задължителна
// снимка и статус на плащането (задържано → освободено при двойно потвърждение).

import React, { useCallback, useState } from 'react';
import { Image, Pressable, ScrollView, Text, View } from 'react-native';
import * as ImagePicker from 'expo-image-picker';
import { useFocusEffect } from '@react-navigation/native';
import { api, API_URL, money } from '../../api';
import { showError, showSuccess } from '../../toast';
import { showPhoto } from '../../photo-viewer';
import { Badge, Button, Card, COLORS, Input, Label, Muted, Screen, Title } from '../../ui';
import { compressAndUploadPhoto } from '../../photo-picker';
import { placeLabel } from '../../geo';
import { getLocale, t } from '../../i18n';

const requestStatus = (): Record<string, [string, string]> => ({
  SENT: [t('Ново запитване'), '#D97706'],
  OFFERED: [t('Изпратена оферта'), COLORS.primary],
  ACCEPTED: [t('Приета — за изпълнение'), COLORS.success],
  DECLINED: [t('Отказана'), COLORS.muted],
  CANCELLED: [t('Затворено'), COLORS.muted],
  COMPLETED: [t('Завършена ✓'), COLORS.success],
});

export default function HandymanRequestsScreen() {
  const [requests, setRequests] = useState<any[]>([]);
  const [trustedFor, setTrustedFor] = useState<any[]>([]);
  const [forms, setForms] = useState<Record<string, { price: string; days: string; message: string }>>({});
  // Комисионни към платформата (ръчен режим): натрупани + фактури
  const [commissions, setCommissions] = useState<any>(null);
  const REQUEST_STATUS = requestStatus();

  const load = useCallback(() => {
    api('/jobs/requests').then((r) => setRequests(r.requests)).catch((e) => showError(e.message));
    api('/jobs/trusted').then((r) => setTrustedFor(r.landlords ?? [])).catch(() => {});
    api('/jobs/my-commissions').then(setCommissions).catch(() => {});
  }, []);
  useFocusEffect(load);

  // Директен (безплатен) поток — приемане, отстраняване, без плащане
  const directAct = async (requestId: string, action: 'direct-accept') => {
    try {
      await api(`/jobs/requests/${requestId}/${action}`, { body: {} });
      showSuccess(t('Заявката е приета. Разплащането е по ваша лична уговорка.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const directDone = async (requestId: string) => {
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.7 });
    if (result.canceled || !result.assets[0]) return;
    try {
      const photoUrl = await compressAndUploadPhoto(result.assets[0]);
      await api(`/jobs/requests/${requestId}/direct-done`, { body: { photoUrl } });
      showSuccess(t('Отбелязано като отстранено. Двете страни трябва да потвърдят.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const sendOffer = async (requestId: string) => {
    const form = forms[requestId];
    try {
      await api(`/jobs/requests/${requestId}/offer`, {
        body: {
          priceCents: Math.round(Number(form.price.replace(',', '.')) * 100),
          daysToComplete: Number(form.days),
          message: form.message || undefined,
        },
      });
      showSuccess(t('Офертата е изпратена.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  const markDone = async (offerId: string) => {
    const result = await ImagePicker.launchImageLibraryAsync({ mediaTypes: ['images'], quality: 0.7 });
    if (result.canceled || !result.assets[0]) return;
    try {
      const photoUrl = await compressAndUploadPhoto(result.assets[0]);
      await api(`/jobs/offers/${offerId}/done`, { body: { photoUrl } });
      showSuccess(t('Отбелязано като отстранено. Двете страни трябва да потвърдят, за да се освободи плащането.'));
      load();
    } catch (e: any) {
      showError(e.message);
    }
  };

  return (
    <Screen>
      <Title>{t('Заявки за ремонт')}</Title>

      {/* Комисионни към платформата (ръчен режим) */}
      {commissions?.blocked && (
        <Card style={{ borderColor: COLORS.danger, borderWidth: 2 }}>
          <Label>{t('⛔ Просрочена фактура за комисионни')}</Label>
          <Muted>
            {t('Нови заявки и оферти са спрени, докато не платите просрочената фактура по банка. Вижте фактурите по-долу.')}
          </Muted>
        </Card>
      )}
      {commissions && (commissions.accruedCents > 0 || commissions.invoices.length > 0) && (
        <Card>
          <Label>{t('Комисионни към платформата')}</Label>
          {commissions.accruedCents > 0 && (
            <Muted>
              {t('Натрупани от приети ремонти: {amount} ({n} ремонта) — ще получите фактура.', {
                amount: money(commissions.accruedCents),
                n: commissions.accruedCount,
              })}
            </Muted>
          )}
          {commissions.invoices.map((inv: any) => (
            <View key={inv.id} style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', paddingVertical: 6, borderTopWidth: 1, borderTopColor: COLORS.border }}>
              <View style={{ flex: 1 }}>
                <Text style={{ color: COLORS.text }}>{t('Фактура № {number}', { number: String(inv.number).padStart(6, '0') })} · {money(inv.totalCents)}</Text>
                <Muted>{t('падеж {date}', { date: new Date(inv.dueDate).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}</Muted>
              </View>
              <Badge
                text={inv.status === 'PAID' ? t('Платена ✓') : inv.overdue ? t('ПРОСРОЧЕНА') : t('За плащане')}
                color={inv.status === 'PAID' ? COLORS.success : inv.overdue ? COLORS.danger : '#D97706'}
              />
            </View>
          ))}
        </Card>
      )}

      {trustedFor.length > 0 && (
        <Card style={{ borderColor: COLORS.success, borderWidth: 2 }}>
          <Label>{t('⭐ Доверен майстор сте при:')}</Label>
          {trustedFor.map((l) => (
            <Muted key={l.id}>
              {l.agencyProfile?.companyName ?? `${l.firstName} ${l.lastName}`} ·{' '}
              {t('от {date}', { date: new Date(l.since).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG') })}
            </Muted>
          ))}
          <Muted>{t('Те могат да ви пращат заявки директно — без оферта и без плащане през приложението.')}</Muted>
        </Card>
      )}

      {requests.length === 0 && (
        <Card>
          <Muted>{t('Още нямате запитвания. Наемодателите ви откриват по специалност и район.')}</Muted>
        </Card>
      )}
      {requests.map((r) => {
        const [label, color] = REQUEST_STATUS[r.status] ?? [r.status, COLORS.muted];
        const escrow = r.offer?.escrowPayment;
        const form = forms[r.id] ?? { price: '', days: '', message: '' };
        return (
          <Card key={r.id} style={r.isDirect ? { borderColor: COLORS.success, borderWidth: 2 } : {}}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
              <Badge text={label} color={color} />
              <Muted>{new Date(r.createdAt).toLocaleDateString(getLocale() === 'en' ? 'en-GB' : 'bg-BG')}</Muted>
            </View>
            {r.isDirect && (
              <Badge text={t('⭐ Директна заявка — без плащане през приложението')} color={COLORS.success} />
            )}
            <Text style={{ fontSize: 16, fontWeight: '600', color: COLORS.text, marginTop: 6 }}>
              {r.issue.description}
            </Text>
            <Muted>{placeLabel(r.issue.apartment.city, r.issue.apartment.region)}{r.message ? ` · „${r.message}“` : ''}</Muted>
            {r.issue.photos?.length > 0 && (
              <ScrollView horizontal style={{ marginTop: 8 }}>
                {r.issue.photos.map((p: string) => (
                  <Pressable key={p} onPress={() => showPhoto(`${API_URL}${p}`)}>
                    <Image source={{ uri: `${API_URL}${p}` }} style={{ width: 80, height: 80, borderRadius: 8, marginRight: 8 }} />
                  </Pressable>
                ))}
              </ScrollView>
            )}

            {/* --- ДИРЕКТЕН поток (доверен майстор): без оферта и без пари --- */}
            {r.isDirect && r.status === 'SENT' && (
              <>
                <Muted>{t('Разплащането е по ваша лична уговорка с наемодателя — извън приложението.')}</Muted>
                <Button title={t('Приемам заявката')} onPress={() => directAct(r.id, 'direct-accept')} />
              </>
            )}
            {r.isDirect && r.status === 'ACCEPTED' && !r.markedDoneAt && (
              <Button title={t('Отстранено е — качи снимка (задължителна)')} onPress={() => directDone(r.id)} />
            )}
            {r.isDirect && r.markedDoneAt && r.status !== 'COMPLETED' && (
              <Badge text={t('Чака потвърждение от наемодателя и наемателя')} color="#D97706" />
            )}
            {r.isDirect && r.status === 'COMPLETED' && (
              <Badge text={t('Потвърдено от двете страни ✓')} color={COLORS.success} />
            )}

            {/* --- ПЛАТЕН поток: оферта → escrow → двойно потвърждение --- */}
            {!r.isDirect && r.status === 'SENT' && (
              <>
                <Label>{t('Вашата оферта')}</Label>
                <View style={{ flexDirection: 'row', gap: 8 }}>
                  <View style={{ flex: 1 }}>
                    <Input
                      placeholder={t('Цена (€)')}
                      keyboardType="decimal-pad"
                      value={form.price}
                      onChangeText={(v) => setForms((f) => ({ ...f, [r.id]: { ...form, price: v } }))}
                    />
                  </View>
                  <View style={{ flex: 1 }}>
                    <Input
                      placeholder={t('Срок (дни)')}
                      keyboardType="number-pad"
                      value={form.days}
                      onChangeText={(v) => setForms((f) => ({ ...f, [r.id]: { ...form, days: v } }))}
                    />
                  </View>
                </View>
                <Input
                  placeholder={t('Съобщение (по избор)')}
                  value={form.message}
                  onChangeText={(v) => setForms((f) => ({ ...f, [r.id]: { ...form, message: v } }))}
                  style={{ marginTop: 8 }}
                />
                <Button title={t('Изпрати оферта')} onPress={() => sendOffer(r.id)} disabled={!form.price || !form.days} />
              </>
            )}

            {r.offer && (
              <Muted>
                {t('Оферта: {price} · срок {days} дни', { price: money(r.offer.priceCents), days: r.offer.daysToComplete })}
              </Muted>
            )}

            {!r.isDirect && r.status === 'ACCEPTED' && escrow && !escrow.markedDoneAt && (
              <>
                <Badge text={t('Задържани {amount} — чакат изпълнение', { amount: money(escrow.amountCents) })} color={COLORS.primary} />
                <Button title={t('Отстранено е — качи снимка (задължителна)')} onPress={() => markDone(r.offer.id)} />
              </>
            )}
            {escrow?.markedDoneAt && escrow.status === 'HELD' && (
              <Badge text={t('Чака потвърждение от наемодателя и наемателя')} color="#D97706" />
            )}
            {escrow?.status === 'RELEASED' && <Badge text={t('Платено ✓ {amount}', { amount: money(escrow.amountCents) })} color={COLORS.success} />}
            {escrow?.status === 'DISPUTED' && <Badge text={t('В спор — при модератор')} color={COLORS.danger} />}
            {escrow?.status === 'REFUNDED' && <Badge text={t('Върнато на наемодателя')} color={COLORS.danger} />}
          </Card>
        );
      })}
    </Screen>
  );
}
